import {
  reactive,
  ref,
  computed,
  useContext,
  useRoute,
} from "@nuxtjs/composition-api";
import {
  FAVOURITE_ENTITY_TYPE_LIST,
  FAVOURITE_ENTITY_TYPES,
} from "./favouriteEntityTypes";
import { useUserStore } from "~/stores/user";

import MY_FAVOURITES from "~/graphql/myFavourites.graphql";
import ADD_FAVOURITE from "~/graphql/mutations/addFavourite.graphql";
import REMOVE_FAVOURITE from "~/graphql/mutations/removeFavourite.graphql";

/**
 * Module-scoped state, shared across every component that calls useFavourites().
 *
 * `cache` maps each entity type to an array of favourited entity ids. All keys
 * are initialised up front because Vue 2's reactivity system does not track
 * properties added to an object after it becomes reactive.
 *
 * `favourites` holds the full PersonFavourite objects (with the resolved entity
 * union), keyed by entity type, so the My Favourites page can render them
 * without an extra fetch.
 */
function emptyByType(value) {
  return FAVOURITE_ENTITY_TYPE_LIST.reduce((acc, type) => {
    acc[type] = value();
    return acc;
  }, {});
}

const cache = reactive(emptyByType(() => []));
const favourites = reactive(emptyByType(() => []));
const loaded = ref(false);
const loading = ref(false);

/**
 * Drives the global <FavouriteAuthDialog>. When an anonymous user tries to
 * favourite something we stash the intended action in `pendingFavourite` and
 * open the dialog instead of navigating away; once they authenticate the
 * pending favourite is applied in place (see `applyPendingFavourite`).
 */
const authDialogOpen = ref(false);
const pendingFavourite = ref(null);

/**
 * Tracks the in-flight full favourites load kicked off by the auth store on
 * login. `applyPendingFavourite` awaits it before adding, so the load's
 * pre-add snapshot can't seed over (and revert) the favourite just added.
 */
let favouritesLoadPromise = null;

export function setFavouritesLoadPromise(promise) {
  favouritesLoadPromise = promise;
  promise.finally(() => {
    if (favouritesLoadPromise === promise) {
      favouritesLoadPromise = null;
    }
  });
  return promise;
}

function isUnauthenticatedError(error) {
  const errors = error?.response?.errors ?? error?.graphQLErrors ?? [];
  return errors.some((e) => e?.extensions?.code === "UNAUTHENTICATED");
}

/**
 * Replaces the shared cache with the given list of PersonFavourite objects.
 * Module-level so it can be called outside a component setup (e.g. from the
 * auth store after the user is loaded). When `types` is provided only those
 * entity-type buckets are replaced; otherwise every bucket is reset first.
 */
export function seedFavourites(list = [], types = null) {
  const resetTypes = types ?? FAVOURITE_ENTITY_TYPE_LIST;
  resetTypes.forEach((type) => {
    cache[type] = [];
    favourites[type] = [];
  });

  (list ?? []).forEach((fav) => {
    const type = fav.entityType;
    if (!cache[type]) {
      return;
    }
    const id = String(fav.entity?.id ?? "");
    if (id && !cache[type].includes(id)) {
      cache[type] = [...cache[type], id];
    }
    favourites[type] = [...favourites[type], fav];
  });
}

export function resetFavouritesCache() {
  FAVOURITE_ENTITY_TYPE_LIST.forEach((type) => {
    cache[type] = [];
    favourites[type] = [];
  });
  loaded.value = false;
}

export function useFavourites() {
  const { $query, $mutate, $helpers, $logger } = useContext();
  const route = useRoute();
  const userStore = useUserStore();

  const isLoggedIn = computed(() => !!userStore.me);

  /**
   * Opens the auth dialog, remembering the favourite the user was trying to
   * add so it can be applied once they are logged in.
   */
  function promptAuth(entityType, entityId, url) {
    pendingFavourite.value = { entityType, entityId, url };
    authDialogOpen.value = true;
  }

  function closeAuthDialog() {
    authDialogOpen.value = false;
    pendingFavourite.value = null;
  }

  function isFavourite(entityType, entityId) {
    return cache[entityType]?.includes(String(entityId)) ?? false;
  }

  function addToCache(entityType, entityId) {
    const id = String(entityId);
    if (!cache[entityType].includes(id)) {
      cache[entityType] = [...cache[entityType], id];
    }
  }

  function removeFromCache(entityType, entityId) {
    const id = String(entityId);
    cache[entityType] = cache[entityType].filter((e) => e !== id);
    favourites[entityType] = favourites[entityType].filter(
      (fav) => String(fav.entity?.id ?? fav.entityId) !== id
    );
  }

  /**
   * Loads the logged-in user's favourites and seeds the cache.
   * Pass an entityType to load just that type, or omit for all types.
   */
  async function loadFavourites(entityType = null) {
    if (!isLoggedIn.value) {
      return;
    }
    try {
      loading.value = true;
      const { myFavourites } = await $query(MY_FAVOURITES, { entityType });
      seedFavourites(myFavourites, entityType ? [entityType] : null);
      if (!entityType) {
        loaded.value = true;
      }
    } catch (error) {
      $logger.error("Failed to load favourites", error);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Builds the full URL stored alongside the favourite.
   * Defaults to the current page; callers may pass an explicit url.
   */
  function buildUrl(explicitUrl) {
    if (explicitUrl) {
      return explicitUrl;
    }
    const fullPath = route.value?.fullPath ?? "";
    return `${$helpers.topDomain}${fullPath}`;
  }

  /**
   * Toggles a favourite. Anonymous users are shown the auth dialog (favourites
   * only exist server-side); the action is remembered and applied once they log
   * in. Optimistic update with revert-on-error.
   *
   * Returns the resulting favourited state (true/false), or null if the auth
   * dialog was opened instead.
   */
  async function toggle(entityType, entityId, { url } = {}) {
    if (!isLoggedIn.value) {
      promptAuth(entityType, entityId, buildUrl(url));
      return null;
    }

    const wasFavourite = isFavourite(entityType, entityId);

    // optimistic
    if (wasFavourite) {
      removeFromCache(entityType, entityId);
    } else {
      addToCache(entityType, entityId);
    }

    try {
      if (wasFavourite) {
        await $mutate(
          REMOVE_FAVOURITE,
          { input: { entityType, entityId: String(entityId) } },
          { showErrorToast: true }
        );
      } else {
        await $mutate(
          ADD_FAVOURITE,
          { input: { entityType, entityId: String(entityId), url: buildUrl(url) } },
          { showErrorToast: true }
        );
      }
      return !wasFavourite;
    } catch (error) {
      // revert optimistic update
      if (wasFavourite) {
        addToCache(entityType, entityId);
      } else {
        removeFromCache(entityType, entityId);
      }
      if (isUnauthenticatedError(error)) {
        promptAuth(entityType, entityId, buildUrl(url));
        return null;
      }
      $logger.error("Failed to toggle favourite", error);
      return wasFavourite;
    }
  }

  /**
   * Called by the auth dialog after a successful login/registration. Loads the
   * freshly-authenticated user's favourites for the pending entity type, then
   * adds the pending favourite if it isn't already saved. Closes the dialog.
   */
  async function applyPendingFavourite() {
    const pending = pendingFavourite.value;
    closeAuthDialog();
    if (!pending || !isLoggedIn.value) {
      return;
    }
    // The login flow kicks off a full favourites load. Wait for it to finish so
    // its pre-add snapshot can't seed over (and revert) the favourite we add
    // below. Fall back to loading just this entity type if none is in flight.
    if (favouritesLoadPromise) {
      await favouritesLoadPromise;
    } else {
      await loadFavourites(pending.entityType);
    }
    if (!isFavourite(pending.entityType, pending.entityId)) {
      await toggle(pending.entityType, pending.entityId, { url: pending.url });
    }
  }

  const favouriteCount = computed(() =>
    FAVOURITE_ENTITY_TYPE_LIST.reduce((sum, type) => sum + cache[type].length, 0)
  );

  function reset() {
    resetFavouritesCache();
  }

  return {
    FAVOURITE_ENTITY_TYPES,
    cache,
    favourites,
    loaded,
    loading,
    isLoggedIn,
    isFavourite,
    toggle,
    loadFavourites,
    favouriteCount,
    reset,
    authDialogOpen,
    closeAuthDialog,
    applyPendingFavourite,
  };
}
